import { Minus, Plus, X } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useGameSettings } from '../../slotGame/slotData'

const BetMultiplier = ({ onClose, betAction, bet, setBet, lineForBet, balance }) => {

  const { gameBackgroundUrl, maxBet, minBet } = useGameSettings()

  const coinValueOptionsRef = useRef([0.01, 0.05, 0.07, 0.10, 0.20, 0.50, 1.20]); // initialize ref with array copy
  const [betValue, setBetValue] = useState(5)
  const [coinValue, setCoinValue] = useState(coinValueOptionsRef.current[1])
  const [plusDisable, setPlusDisable] = useState(false)
  const [minusDisable, setMinusDisable] = useState(false)

  const totalBet = useMemo(() => parseFloat((betValue * coinValue * lineForBet).toFixed(2)), [betValue, coinValue, lineForBet]);

  useEffect(() => {
    if (Math.round(totalBet) === maxBet) {
      setBet(Math.round(totalBet))
    } else {
      setBet(totalBet);
    }
  }, [totalBet, setBet, maxBet]);

  useEffect(() => {
    if (bet >= 0) {
      const rawBetValue = bet / lineForBet;
      let roundedBetValue = Math.round(rawBetValue);

      // Prevent division by zero
      if (roundedBetValue === 0) roundedBetValue = 1;

      const coinValue = parseFloat((rawBetValue / roundedBetValue).toFixed(4));

      if (!coinValueOptionsRef.current.includes(coinValue)) {
        coinValueOptionsRef.current.push(coinValue);
        coinValueOptionsRef.current.sort((a, b) => a - b);
      }

      setBetValue(roundedBetValue);
      setCoinValue(coinValue);
    } else {
      setBetValue(5)
      setCoinValue(0)
      setBet(totalBet)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function to increment/decrement betValue
  const changeBetValue = (increment) => {
    if (increment) {
      setBetValue(betValue + 1)
    } else if (!increment) {
      if (betValue - 1 !== 0) {
        setBetValue(betValue - 1)
      }
    }
  }

  // Function to increment/decrement coinValue
  const changeCoinValue = (increment) => {
    const currentIndex = coinValueOptionsRef.current.indexOf(coinValue)
    if (increment && currentIndex < coinValueOptionsRef.current.length - 1) {
      setCoinValue(coinValueOptionsRef.current[currentIndex + 1])
    } else if (!increment && currentIndex > 0) {
      setCoinValue(coinValueOptionsRef.current[currentIndex - 1])
    }
  }


  const handleBetMax = useCallback(() => {
    // let clampedBet = Math.min(maxBet, balance);
    // let actualBetMax = Math.floor(clampedBet);
    let clampedBet = maxBet
    let actualBetMax = Math.floor(maxBet);
    let finalBetValue = 0;
    let finalCoinValue = 0;

    while (actualBetMax > 0) {
      const rawBetValue = actualBetMax / lineForBet;
      const roundedBetValue = Math.round(rawBetValue);
      const coinValue = parseFloat((rawBetValue / roundedBetValue).toFixed(4));
      const totalBet = roundedBetValue * coinValue * lineForBet;

      if (totalBet <= clampedBet) {
        finalBetValue = roundedBetValue;
        finalCoinValue = coinValue;

        if (!coinValueOptionsRef.current.includes(coinValue)) {
          coinValueOptionsRef.current.push(coinValue);
          coinValueOptionsRef.current.sort((a, b) => a - b);
        }

        break;
      }

      actualBetMax--;
    }

    setBetValue(finalBetValue);
    setCoinValue(finalCoinValue);
  }, [maxBet, lineForBet]);

  useEffect(() => {
    if (bet <= minBet) {
      setMinusDisable(true)
    } else {
      setMinusDisable(false)
    }
    if (bet >= maxBet) {
      setPlusDisable(true)
      handleBetMax()
    } else {
      setPlusDisable(false)
    }
  }, [bet, handleBetMax, maxBet, minBet])

  useEffect(() => {
    if (bet === balance) {
      setPlusDisable(true)
    }
  }, [balance, bet])

  return (
    <div
      className='fixed bottom-[20%] right-[11%] w-[18%] h-[55%] flex justify-center flex-col bg-[rgba(0,0,0,0.80)] bg-blend-darken bg-contain rounded-2xl'
      style={{
        backgroundImage: `url(${gameBackgroundUrl})`,
        backgroundSize: "100% 100%",
        zIndex: 2,
      }}
    >
      <div className="w-full h-[15%] text-white flex items-center justify-between relative xs:text-[0.55rem] sm:text-[0.65rem] md:text-[0.72rem] lg:text-sm xl:text-lg 2xl:text-2xl 3xl:text-3xl font-bold border-b border-gray-500">
        <span className="xs:ml-1 md:ml-1.5 lg:ml-4 3xl:ml-10">Bet Multiplier {lineForBet}x</span>
        <button
          className="absolute xs:right-1 md:right-1 lg:right-3 3xl:right-5 text-gray-50 bg-slate-700 xs:p-0 md:p-1 hover:text-gray-300 text-xl rounded-full"
          onClick={onClose}
        >
          <X className="xs:w-3 xs:h-3 lg:w-5 lg:h-5 xl:w-6 xl:h-6 2xl:w-8 2xl:h-8" />
        </button>
      </div>
      <div className='w-full h-[85%] xs:mt-1 xl:mt-4 flex flex-col items-center justify-start text-white'>

        <div className='flex flex-col items-start w-[80%] h-[25%]'>
          <span className='xs:text-[0.50rem] sm:text-[0.65rem] md:text-[0.72rem] lg:text-sm xl:text-lg 2xl:text-2xl 3xl:text-3xl'>Bet</span>

          <div className='flex flex-row items-center justify-center w-full xs:gap-1 xl:gap-2 2xl:gap-3 mt-1'>
            {/* Minus Button */}
            <button
              className={`${minusDisable ? "bg-gray-700" : "bg-white"} rounded-full flex items-center justify-center 
                 xs:h-4 xs:w-4 sm:h-5 sm:w-5 md:h-7 md:w-7 lg:h-10 lg:w-10 xl:h-12 xl:w-12 3xl:h-16 3xl:w-16`}
              onClick={() => { if (!minusDisable) { changeBetValue(false); } }}
            >
              <Minus className="w-full h-full p-[10%]" color={`${minusDisable ? "white" : "black"}`} />
            </button>

            {/* Bet Value Box */}
            <div
              className='rounded-2xl bg-[#262E3A] border border-gray-500 font-bold flex items-center justify-center
                xs:h-4 sm:h-5 md:h-7 lg:h-10 xl:h-12 3xl:h-16
                xs:text-[0.65rem] sm:text-xs md:text-md lg:text-lg xl:text-xl 2xl:text-2xl 3xl:text-3xl
                flex-1 text-center'
            >
              {betValue}
            </div>

            {/* Plus Button */}
            <button
              className={`${plusDisable ? "bg-gray-700" : "bg-[#00B762]"} rounded-full flex items-center justify-center
                  xs:h-4 xs:w-4 sm:h-5 sm:w-5 md:h-7 md:w-7 lg:h-10 lg:w-10 xl:h-12 xl:w-12 3xl:h-16 3xl:w-16`}
              onClick={() => { if (!plusDisable) { changeBetValue(true) } }}
            >
              <Plus className="w-full h-full p-[10%]" />
            </button>
          </div>
        </div>

        <div className='flex flex-col items-start w-[80%] h-[25%]'>
          <span className='xs:text-[0.50rem] sm:text-[0.65rem] md:text-[0.72rem] lg:text-sm xl:text-lg 2xl:text-2xl 3xl:text-3xl'>Coin value</span>

          <div className='flex flex-row items-center justify-center w-full xs:gap-1 xl:gap-2 2xl:gap-3 mt-1'>
            {/* Minus Button */}
            <button
              className={`${minusDisable ? "bg-gray-700" : "bg-white"} rounded-full flex items-center justify-center 
             xs:h-4 xs:w-4 sm:h-5 sm:w-5 md:h-7 md:w-7 lg:h-10 lg:w-10 xl:h-12 xl:w-12 3xl:h-16 3xl:w-16`}
              onClick={() => { if (!minusDisable) { changeCoinValue(false) } }}
            >
              <Minus className="w-full h-full p-[10%]" color={`${minusDisable ? "white" : "black"}`} />
            </button>

            {/* Bet Value Box */}
            <div
              className='rounded-2xl bg-[#262E3A] border border-gray-500 font-bold flex items-center justify-center
                xs:h-4 sm:h-5 md:h-7 lg:h-10 xl:h-12 3xl:h-16
                xs:text-[0.65rem] sm:text-xs md:text-md lg:text-lg xl:text-xl 2xl:text-2xl 3xl:text-3xl
                flex-1 text-center'
            >
              {coinValue?.toFixed(3)}
            </div>

            {/* Plus Button */}
            <button
              className={`${plusDisable ? "bg-gray-700" : "bg-[#00B762]"} rounded-full flex items-center justify-center
              xs:h-4 xs:w-4 sm:h-5 sm:w-5 md:h-7 md:w-7 lg:h-10 lg:w-10 xl:h-12 xl:w-12 3xl:h-16 3xl:w-16`}
              onClick={() => { if (!plusDisable) { changeCoinValue(true) } }}
            >
              <Plus className="w-full h-full p-[10%]" />
            </button>
          </div>
        </div>

        <div className='flex flex-col items-start w-[80%] h-[25%]'>
          <span className='xs:text-[0.50rem] sm:text-[0.65rem] md:text-[0.72rem] lg:text-sm xl:text-lg 2xl:text-2xl 3xl:text-3xl'>Total Bet Amount</span>

          <div className='flex flex-row items-center justify-center w-full xs:gap-1 xl:gap-2 2xl:gap-3 xs:mt-0 md:mt-1'>
            {/* Minus Button */}
            <button
              className={`${minusDisable ? "bg-gray-700" : "bg-white"} rounded-full flex items-center justify-center 
            xs:h-4 xs:w-4 sm:h-5 sm:w-5 md:h-7 md:w-7 lg:h-10 lg:w-10 xl:h-12 xl:w-12 3xl:h-16 3xl:w-16`}
              onClick={() => { if (!minusDisable) { changeBetValue(false) } }}
            >
              <Minus className="w-full h-full p-[10%]" color={`${minusDisable ? "white" : "black"}`} />
            </button>

            {/* Bet Value Box */}
            <div
              className='rounded-2xl bg-[#262E3A] border border-gray-500 font-bold flex items-center justify-center
                xs:h-4 sm:h-5 md:h-7 lg:h-10 xl:h-12 3xl:h-16
                xs:text-[0.65rem] sm:text-xs md:text-md lg:text-lg xl:text-xl 2xl:text-2xl 3xl:text-3xl
                flex-1 text-center'
            >
              {Math.round(bet) === maxBet ? Math.round(bet) : bet}
            </div>

            {/* Plus Button */}
            <button
              className={`${plusDisable ? "bg-gray-700" : "bg-[#00B762]"} rounded-full flex items-center justify-center
              xs:h-4 xs:w-4 sm:h-5 sm:w-5 md:h-7 md:w-7 lg:h-10 lg:w-10 xl:h-12 xl:w-12 3xl:h-16 3xl:w-16`}
              onClick={() => { if (!plusDisable) { changeBetValue(true) } }}
            >
              <Plus className="w-full h-full p-[10%]" />
            </button>
          </div>
        </div>

        <div
          className='w-[80%] xs:h-[10%] xs:mt-1 lg:mt-2 flex items-center justify-center bg-[#F8B731] rounded-2xl text-black font-bold xs:text-[0.55rem] sm:text-[0.65rem] md:text-[0.72rem] lg:text-sm xl:text-lg 2xl:text-2xl 3xl:text-3xl'
          onClick={handleBetMax}
        >
          Bet Max
        </div>
      </div>
      <div
        className={`absolute bottom-[-16px] ${betAction === "Plus" ? "right-[10%]" : "left-[10%]"} w-0 h-0 border-l-[14px] border-r-[14px] border-t-[16px] border-l-transparent border-r-transparent border-t-[#000000d9]`}
      />
    </div>
  )
}

export default BetMultiplier